import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiggyBank } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { money } from '../../../utils/nurseryMoney';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';
import type { CustomerDeposit, CustomerDeposits } from '../../../types/nurseryAccounts';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500';

/**
 * A customer's deposits, on its Account tab: what its payer holds, every
 * deposit, and asking for one with no order (Rob, 2026-10-16). A customer
 * who has already paid gets a deposit invoice that is not emailed: it sits in
 * Xero for the payment to be applied to, and once Xero says it is paid the
 * customer's next invoices take it off. Nothing here records a payment.
 */
export function CustomerDepositsCard({ customerId }: { customerId: number }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CustomerDepositsCard' });
  const tz = useOrgTimezone();
  const [data, setData] = useState<CustomerDeposits | null>(null);
  const [asking, setAsking] = useState(false);
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<CustomerDeposit | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryAccountsService.getCustomerDeposits(customerId);
      if (res.success && res.data) setData(res.data);
    } catch {
      // The rest of the account still shows; the card stays away.
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const reset = () => {
    setAsking(false);
    setAmount('');
    setDueDate('');
    setNotes('');
    setEmail(true);
    setError(null);
  };

  const ask = async () => {
    const value = Math.round(Number(amount.replace(/[$,\s]/g, '')) * 100) / 100;
    if (!(value > 0)) { setError(t('amountNeeded')); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.requestCustomerDeposit(customerId, {
        amount: value,
        due_date: dueDate || null,
        notes: notes.trim() || null,
        email_customer: email,
      });
      if (!res.success || !res.data) throw new Error(errorMessage(res, t('requestFailed')));
      setDone(res.data);
      reset();
      await load();
    } catch (e) {
      setError(errorMessage(e, t('requestFailed')));
    }
    setBusy(false);
  };

  const retry = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.retryDepositXero(id);
      if (!res.success) throw new Error(errorMessage(res, t('retryFailed')));
      await load();
    } catch (e) {
      setError(errorMessage(e, t('retryFailed')));
    }
    setBusy(false);
  };

  const where = (d: CustomerDeposit): string => {
    if (d.status === 'void') return t('stateVoid', { reason: d.void_reason || '' });
    if (d.status === 'paid') return d.drawn > 0 ? t('statePaidDrawn', { drawn: money(d.drawn), left: money(d.available) }) : t('statePaid');
    if (d.xero_sync_status === 'failed') return t('stateFailed', { error: d.xero_sync_error || '' });
    if (!d.xero_invoice_id) return t('stateSending');
    if (d.amount_paid > 0) return t('statePart', { paid: money(d.amount_paid), amount: money(d.amount) });
    return d.email_customer === false ? t('stateApply') : t('stateWaiting');
  };

  const held = data.held;
  const payer = data.payer.name;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
          <PiggyBank className="h-5 w-5 text-primary-600 shrink-0" /> {t('title')}
        </h2>
        <Link to={`/accounts/deposits?payer=${data.payer.id}`} className="text-sm text-primary-700 hover:underline">
          {t('allDeposits')}
        </Link>
      </div>

      {!data.pays_for_itself && (
        <p className="text-sm text-neutral-700 break-words">{t('heldOnPayer', { payer })}</p>
      )}

      <p className="text-sm text-neutral-700 break-words">
        {held.available > 0 || held.outstanding > 0
          ? t('held', { payer, available: money(held.available), outstanding: money(held.outstanding) })
          : t('none', { payer })}
      </p>

      {done && (
        <p className="text-sm text-green-900 bg-green-50 border border-green-200 rounded-lg px-3 py-2 break-words">
          {done.email_customer === false
            ? t('doneNotSent', { number: done.deposit_number || t('deposit') })
            : t('doneSent', { number: done.deposit_number || t('deposit') })}
        </p>
      )}

      {error && <p className="text-sm text-red-700 break-words">{error}</p>}

      {data.deposits.length > 0 && (
        <ul className="space-y-2">
          {data.deposits.map((d) => (
            <li key={d.id} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="font-medium text-neutral-900 break-all">{d.deposit_number || t('deposit')}</span>
                <span className="text-neutral-900">{money(d.amount)}</span>
              </div>
              <p className="text-neutral-600 break-words">
                {d.order_id && d.order_number
                  ? <><Link to={`/nursery/orders/${d.order_id}`} className="text-primary-700 hover:underline">{t('forOrder', { order: d.order_number })}</Link>{' · '}</>
                  : d.notes ? `${d.notes} · ` : ''}
                {formatLocalDateInTz(String(d.deposit_date).slice(0, 10), tz)}
                {d.email_customer === false ? ` · ${t('notEmailed')}` : ''}
              </p>
              <p className="text-neutral-600 break-words">{where(d)}</p>
              {d.xero_sync_status === 'failed' && (
                <Button size="sm" variant="secondary" className="mt-2" disabled={busy} onClick={() => retry(d.id)}>
                  {t('retry')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!data.xero_active && <p className="text-sm text-neutral-700 break-words">{t('connectXero')}</p>}

      {!asking ? (
        <Button size="sm" onClick={() => { setDone(null); setAsking(true); }} disabled={!data.xero_active}>
          {t('ask')}
        </Button>
      ) : (
        <div className="space-y-3 border-t border-neutral-200 pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
            <div>
              <label htmlFor="customer-deposit-amount" className="block text-sm font-medium text-neutral-800 mb-1">{t('amountLabel')}</label>
              <input id="customer-deposit-amount" type="text" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="customer-deposit-due" className="block text-sm font-medium text-neutral-800 mb-1">{t('dueLabel')}</label>
              <input id="customer-deposit-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
            </div>
          </div>
          <p className="text-xs text-neutral-500 break-words">{t('dueHint', { days: data.due_days })}</p>
          <div>
            <label htmlFor="customer-deposit-notes" className="block text-sm font-medium text-neutral-800 mb-1">{t('notesLabel')}</label>
            <input id="customer-deposit-notes" type="text" value={notes} maxLength={255}
              onChange={(e) => setNotes(e.target.value)} placeholder={t('notesPlaceholder')} className={inputCls} />
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-neutral-800">{t('sendLegend')}</legend>
            <label className="flex items-start gap-2 text-sm text-neutral-800">
              <input type="radio" name="customer-deposit-email" className="mt-1" checked={email} onChange={() => setEmail(true)} />
              <span className="break-words">{t('sendEmail')}</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-neutral-800">
              <input type="radio" name="customer-deposit-email" className="mt-1" checked={!email} onChange={() => setEmail(false)} />
              <span className="break-words">{t('sendNone')}</span>
            </label>
          </fieldset>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={ask} disabled={busy}>{busy ? t('working') : t('confirm')}</Button>
            <Button size="sm" variant="secondary" onClick={reset} disabled={busy}>{t('cancel')}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDepositsCard;
